import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Alert,
  Divider,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Container,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  ArrowRight as ArrowRightIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../api';

interface Company {
  id: number;
  name: string;
  address: string;
  contact: string;
  created_at: string;
}

interface Site {
  id: number;
  company_id: number;
  name: string;
  created_at: string;
}

const CompanyRegister: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<{ [key: number]: Site[] }>({});
  const [expandedCompany, setExpandedCompany] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact: '',
  });
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [siteName, setSiteName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    // 모든 업체의 현장 정보를 가져옵니다.
    companies.forEach(company => {
      fetchSites(company.id);
    });
  }, [companies]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/companies');
      if (Array.isArray(response.data)) {
        setCompanies(response.data);
        const sitesMap: Record<number, Site[]> = {};
        for (const company of response.data) {
          const sitesResponse = await api.get(`/api/companies/${company.id}/sites`);
          sitesMap[company.id] = Array.isArray(sitesResponse.data) ? sitesResponse.data : [];
        }
        setSites(sitesMap);
      } else {
        setCompanies([]);
        setSites({});
      }
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || '업체 목록을 불러오는데 실패했습니다.');
      setCompanies([]);
      setSites({});
      enqueueSnackbar('업체 목록을 불러오는데 실패했습니다.', { 
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async (companyId: number) => {
    try {
      const response = await api.get(`/api/companies/${companyId}/sites`);
      setSites(prev => ({ ...prev, [companyId]: Array.isArray(response.data) ? response.data : [] }));
    } catch (err: any) {
      enqueueSnackbar('현장 목록을 불러오는데 실패했습니다.', { 
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/companies', formData);
      await fetchCompanies();
      setFormData({
        name: '',
        address: '',
        contact: '',
      });
      setError('');
      enqueueSnackbar('업체가 성공적으로 등록되었습니다.', { 
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (err: any) {
      console.error('업체 등록 오류:', err);
      setError(err.response?.data?.detail || '업체 등록에 실패했습니다.');
      enqueueSnackbar('업체 등록에 실패했습니다.', { 
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyClick = (companyId: number) => {
    if (expandedCompany === companyId) {
      setExpandedCompany(null);
    } else {
      setExpandedCompany(companyId);
      fetchSites(companyId);
    }
  };

  const handleAddSiteClick = (companyId: number) => {
    setSelectedCompanyId(companyId);
    setSiteDialogOpen(true);
  };

  const handleSiteSubmit = async () => {
    if (!selectedCompanyId || !siteName) return;

    setLoading(true);
    try {
      await api.post(`/api/companies/${selectedCompanyId}/sites`, {
        company_id: selectedCompanyId,
        name: siteName,
      });
      await fetchSites(selectedCompanyId);
      setSiteDialogOpen(false);
      setSiteName('');
      setError('');
      enqueueSnackbar('현장이 성공적으로 등록되었습니다.', { 
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (err: any) {
      console.error('현장 등록 오류:', err);
      setError(err.response?.data?.detail || '현장 등록에 실패했습니다.');
      enqueueSnackbar('현장 등록에 실패했습니다.', { 
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!window.confirm('이 업체를 삭제하시겠습니까? 연관된 모든 현장도 함께 삭제됩니다.')) {
      return;
    }

    try {
      await api.delete(`/api/companies/${companyId}`);
      enqueueSnackbar('업체가 성공적으로 삭제되었습니다.', { 
        variant: 'success',
        autoHideDuration: 3000,
      });
      fetchCompanies();
    } catch (error) {
      console.error('업체 삭제 오류:', error);
      enqueueSnackbar('업체 삭제 중 오류가 발생했습니다.', { 
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  const handleDeleteSite = async (siteId: number) => {
    if (!window.confirm('이 현장을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await api.delete(`/api/sites/${siteId}`);
      enqueueSnackbar('현장이 성공적으로 삭제되었습니다.', { 
        variant: 'success',
        autoHideDuration: 3000,
      });
      fetchCompanies();
    } catch (error) {
      console.error('현장 삭제 오류:', error);
      enqueueSnackbar('현장 삭제 중 오류가 발생했습니다.', { 
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* 등록 폼 */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h5" gutterBottom>
              업체 등록
            </Typography>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="업체명"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="주소"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="연락처"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                margin="normal"
                size="small"
              />
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : '등록'}
              </Button>
            </form>
          </Paper>
        </Grid>

        {/* 업체 목록 */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              업체 목록
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>업체명</TableCell>
                      <TableCell align="right">관리</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companies.map((company) => (
                      <React.Fragment key={company.id}>
                        {/* 업체 정보 행 */}
                        <TableRow>
                          <TableCell component="th" scope="row">
                            {company.name}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleAddSiteClick(company.id)}
                              >
                                현장 추가
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteCompany(company.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {/* 현장 정보 행 */}
                        {sites[company.id]?.map((site) => (
                          <TableRow key={site.id} sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ pl: 4 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ArrowRightIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                {site.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteSite(site.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* 현장 추가 다이얼로그 */}
      <Dialog open={siteDialogOpen} onClose={() => setSiteDialogOpen(false)}>
        <DialogTitle>현장 추가</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="현장명"
            fullWidth
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteDialogOpen(false)}>취소</Button>
          <Button onClick={handleSiteSubmit} variant="contained">추가</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompanyRegister; 